import { Injectable } from '@angular/core';
import { CapacitorHttp } from '@capacitor/core';

import { EnvironmentService } from '../shared/services/environment/environment.service';
import { createCodeChallenge, randomString } from './pkce';
import { DEFAULT_EXPIRES_IN_S, ITokenSet } from './token-set';

/** The authorization server answered with an OAuth error (RFC 6749 §5.2), e.g. `invalid_grant`. */
export class OAuthError extends Error {
  constructor(
    public readonly code: string,
    description?: string
  ) {
    super(description ? `${code}: ${description}` : code);
    this.name = 'OAuthError';
  }
}

/** The token endpoint could not be reached or answered with something that is not an OAuth response. */
export class OAuthUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'OAuthUnavailableError';
  }
}

export interface IAuthorizeRequest {
  /** Where to send the browser. */
  url: string;
  /** The `state` FusionAuth echoes back; must match on the redirect. */
  state: string;
  /** The PKCE verifier needed for the code exchange. */
  codeVerifier: string;
}

export interface IAuthorizeOptions {
  /** Pre-fills the e-mail on the FusionAuth login page. */
  loginHint?: string;
  /** `login` forces a fresh login even when FusionAuth still has an SSO session. */
  prompt?: string;
}

interface ITokenResponse {
  access_token?: string;
  refresh_token?: string;
  id_token?: string;
  expires_in?: number;
  error?: string;
  error_description?: string;
}

/**
 * Talks OAuth 2.0 / OIDC to FusionAuth: authorization code flow with PKCE, refresh and logout.
 *
 * Stateless and platform-agnostic: the caller passes the redirect URI it registered with
 * FusionAuth. Requests go through CapacitorHttp so the native apps are not subject to WebView
 * CORS rules; in the browser CapacitorHttp falls back to `fetch`.
 */
@Injectable({ providedIn: 'root' })
export class FusionAuthClient {
  constructor(private readonly _environment: EnvironmentService) {}

  public get clientId(): string {
    return this.config.clientId;
  }

  public async createAuthorizeRequest(
    redirectUri: string,
    options: IAuthorizeOptions = {}
  ): Promise<IAuthorizeRequest> {
    const state = randomString(32);
    const codeVerifier = randomString(64);
    const { challenge, method } = await createCodeChallenge(codeVerifier);

    const params = new URLSearchParams({
      client_id: this.config.clientId,
      response_type: 'code',
      scope: this.config.scope,
      redirect_uri: redirectUri,
      state,
      code_challenge: challenge,
      code_challenge_method: method,
    });
    if (options.loginHint) {
      params.set('login_hint', options.loginHint);
    }
    if (options.prompt) {
      params.set('prompt', options.prompt);
    }

    return { url: `${this.config.issuer}/oauth2/authorize?${params.toString()}`, state, codeVerifier };
  }

  public exchangeCode(code: string, codeVerifier: string, redirectUri: string): Promise<ITokenSet> {
    return this.requestTokens({
      grant_type: 'authorization_code',
      client_id: this.config.clientId,
      code,
      code_verifier: codeVerifier,
      redirect_uri: redirectUri,
    });
  }

  public refresh(refreshToken: string): Promise<ITokenSet> {
    return this.requestTokens({
      grant_type: 'refresh_token',
      client_id: this.config.clientId,
      refresh_token: refreshToken,
      scope: this.config.scope,
    });
  }

  /** Ends the FusionAuth SSO session; the browser is redirected to `postLogoutRedirectUri` afterwards. */
  public logoutUrl(postLogoutRedirectUri: string): string {
    const params = new URLSearchParams({
      client_id: this.config.clientId,
      post_logout_redirect_uri: postLogoutRedirectUri,
    });
    return `${this.config.issuer}/oauth2/logout?${params.toString()}`;
  }

  private get config() {
    const config = this._environment.authConfig;
    if (!config) {
      throw new Error('FusionAuthClient used before the environment was initialised');
    }
    return config;
  }

  private async requestTokens(body: Record<string, string>): Promise<ITokenSet> {
    let status: number;
    let data: ITokenResponse;
    try {
      const response = await CapacitorHttp.request({
        method: 'POST',
        url: `${this.config.issuer}/oauth2/token`,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
        data: new URLSearchParams(body).toString(),
        connectTimeout: 15000,
        readTimeout: 15000,
      });
      status = response.status;
      data = parseJson(response.data);
    } catch (error) {
      throw new OAuthUnavailableError(error?.message ?? 'token request failed');
    }

    if (data?.error) {
      throw new OAuthError(data.error, data.error_description);
    }
    if (status < 200 || status >= 300 || !data?.access_token) {
      throw new OAuthUnavailableError(`token endpoint answered with status ${status}`);
    }

    const expiresIn = data.expires_in ?? DEFAULT_EXPIRES_IN_S;
    return {
      accessToken: data.access_token,
      refreshToken: data.refresh_token ?? null,
      idToken: data.id_token ?? null,
      expiresAt: Date.now() + expiresIn * 1000,
      expiresIn,
    };
  }
}

/** CapacitorHttp parses JSON on its own when the server says so; be tolerant when it does not. */
function parseJson(data: unknown): ITokenResponse {
  if (typeof data !== 'string') {
    return (data ?? {}) as ITokenResponse;
  }
  try {
    return JSON.parse(data);
  } catch {
    return {};
  }
}
